import {
  AccordionGroup,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  MixpanelEvent,
  Tab,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  tabLabelerEnumerated,
  useCallbackRef,
  useMixpanel,
  usePersistedTabs,
  usePortal,
  useTabs,
} from '@talos/kyoko';
import { useCallback, useState } from 'react';
import { useBlotterTableTabCloning } from '../../../providers/AppConfigRedux/useBlotterTableTabCloning';
import { CreditBlotter } from './CreditBlotter';
import { COUNTERPARTY_NOP_TAB_ID, CREDIT_TABS_EXCLUDED_IDS, DEFAULT_CREDIT_TABS } from './defaults';
import { CREDIT_BLOTTER_PREFIX } from './tokens';
import type { CreditTabProps } from './types';

const tabLabeler = tabLabelerEnumerated('Credit');
function getBlotterID(tabID: string | undefined) {
  return `${CREDIT_BLOTTER_PREFIX}/${tabID}`;
}

export function Credit() {
  const mixpanel = useMixpanel();

  const [initialFiltersOpen, setInitialFiltersOpen] = useState(false);

  const handleSelect = useCallback(() => setInitialFiltersOpen(false), []);
  const handleAdd = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.AddTab);
    setInitialFiltersOpen(true);
  });

  const persistedTabs = usePersistedTabs<CreditTabProps>(CREDIT_BLOTTER_PREFIX, {
    defaultInitialItems: DEFAULT_CREDIT_TABS,
    defaultInitialSelectedIndex: 0,
    excludedIDs: CREDIT_TABS_EXCLUDED_IDS,
    onSelect: handleSelect,
  });

  const tabs = useTabs<CreditTabProps>({
    ...persistedTabs,
    showAddTab: true,
    tabLabeler,
    onAdd: handleAdd,
    onClone: useBlotterTableTabCloning({ getBlotterID }),
  });

  const handleCloneTab = useCallback(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    tabs.clone(tabs.selectedIndex, {
      closable: true,
      reorderable: true,
    });
  }, [tabs, mixpanel]);

  const handleUpdateTab = useCallbackRef((updatedTab: CreditTabProps) => {
    tabs.updateItem(updatedTab);
  });

  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  return (
    <Tabs {...tabs} h="100%" size={TabSize.Large}>
      <TabList isBordered rightItems={<Box ref={filtersContainerRef} />}>
        {tabs.items.map((tab, idx) => (
          <Tab key={idx} {...tab} reorderable={tab.id !== COUNTERPARTY_NOP_TAB_ID} />
        ))}
      </TabList>
      <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
        {tabs.items.map(tab => (
          <AccordionGroup key={tab.id}>
            <CreditBlotter
              key={tab.id}
              blotterID={getBlotterID(tab.id)}
              tab={tab}
              onUpdateTab={handleUpdateTab}
              onCloneTab={handleCloneTab}
              initialIsOpen={initialFiltersOpen}
            />
          </AccordionGroup>
        ))}
      </TabPanels>
    </Tabs>
  );
}
